import { useState } from 'react'
import { api, ApiError, storage, TOKEN_KEY } from './api'
import { useFeatures } from './features'
import { useI18n } from './i18n'
import { LegalLinks } from './legal/LegalPage'
import { navigate } from './router'
import { Icon } from './ui/Icon'
import { LangSwitch } from './ui/LangSwitch'

export function Home() {
  const { t } = useI18n()
  const features = useFeatures()
  const [token, setToken] = useState(() => storage.get(TOKEN_KEY) ?? '')
  const [remember, setRemember] = useState(() => !!storage.get(TOKEN_KEY))
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<'token' | 'rate' | 'server' | null>(null)

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const room = await api.createRoom(token.trim(), name.trim() || t('home.defaultName'))
      if (remember) storage.set(TOKEN_KEY, token.trim())
      else storage.remove(TOKEN_KEY)
      location.assign(`/r/${room.id}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError('token')
      else if (err instanceof ApiError && err.status === 429) setError('rate')
      else setError('server')
    } finally {
      setBusy(false)
    }
  }

  const errorText = { token: t('home.errToken'), rate: t('home.errRate'), server: t('home.errServer') }

  return (
    <main className="home">
      <section className="home__hero">
        <div className="home__top">
          <p className="logo logo--large">chillio</p>
          <LangSwitch />
        </div>
        <h1 className="home__title">{t('home.title')}</h1>
        <p className="home__lead">{t('home.lead')}</p>
        {features?.catalog && (
          <a
            className="btn home__catalog"
            href="/catalog"
            onClick={(e) => {
              e.preventDefault()
              navigate('/catalog')
            }}
          >
            <Icon name="film" /> {t('catalog.homeLink')}
          </a>
        )}
      </section>
      <form className="home__card" onSubmit={create}>
        <h2 className="home__card-title">{t('home.newRoom')}</h2>
        <label className="field">
          <span>{t('home.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={64} placeholder={t('home.namePlaceholder')} />
        </label>
        <label className="field">
          <span>{t('home.token')}</span>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <label className="toggle">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>{t('home.rememberToken')}</span>
        </label>
        <button type="submit" className="btn btn--primary btn--wide" disabled={busy || !token.trim()}>
          {t('home.create')}
        </button>
        {error && <p className="error-text">{errorText[error]}</p>}
        <p className="muted small">{t('home.friendsHint')}</p>
      </form>
      <footer className="home__legal">
        <LegalLinks />
      </footer>
    </main>
  )
}
